/**
 * v4.9.0 — Voucher Audit Reports surface.
 *
 * Lives as a new tab inside VoucherSettings. Reuses the existing voucher
 * audit infrastructure — this is purely an aggregation + scheduling UI.
 *
 * Two responsibilities:
 *  1. Manage scheduled report definitions (list + create + edit + delete + test send)
 *  2. Run an on-demand preview with group_by + filters (uses /voucher-audit/preview)
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
    CalendarClock, Plus, Send, MoreVertical, Trash2, Copy, Pencil,
    Download, FileSpreadsheet, FileText,
} from "lucide-react";
import { SetupGuidanceCard } from "../setup_guide/SetupGuidanceCard";
import { HelpGlyph } from "../setup_guide/HelpGlyph";
import { parseUtc } from '../../lib/datetime';

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const FREQUENCIES = ["daily", "weekly", "monthly"];
const GROUP_BYS = ["employee_code", "employee_name", "location", "organization", "voucher_type", "redemption_status", "date"];
const FORMATS = ["csv", "xlsx"];

const emptySchedule = {
    name: "",
    frequency: "weekly",
    delivery_time: "06:00",
    timezone: "America/New_York",
    days_of_week: ["mon"],
    day_of_month: 1,
    group_by: "employee_code",
    filters: {},
    export_format: "csv",
    recipients: [],
    active: true,
    language: "en",
};

export const VoucherAuditReports = () => {
    const { t, i18n } = useTranslation();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptySchedule);
    const [recipientsRaw, setRecipientsRaw] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/voucher-audit/reports");
            setReports(res.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("voucher_audit.reports.load_failed"));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptySchedule, language: (i18n.language || "en").split("-")[0] });
        setRecipientsRaw("");
        setOpen(true);
    };

    const openEdit = (rpt) => {
        setEditing(rpt);
        setForm({ ...emptySchedule, ...rpt });
        setRecipientsRaw((rpt.recipients || []).join(", "));
        setOpen(true);
    };

    const parseRecipients = () => recipientsRaw.split(",").map(s => s.trim()).filter(Boolean);

    const save = async () => {
        if (!form.name.trim()) { toast.error(t("voucher_audit.builder.name_required")); return; }
        const recipients = parseRecipients();
        if (recipients.length === 0) { toast.error(t("voucher_audit.builder.recipients_required")); return; }
        const payload = { ...form, recipients };
        setBusy(true);
        try {
            if (editing?.id) {
                await api.put(`/voucher-audit/reports/${editing.id}`, payload);
                toast.success(t("voucher_audit.reports.updated"));
            } else {
                await api.post("/voucher-audit/reports", payload);
                toast.success(t("voucher_audit.reports.created"));
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("voucher_audit.reports.save_failed"));
        } finally { setBusy(false); }
    };

    const remove = async (rpt) => {
        if (!window.confirm(t("voucher_audit.reports.delete_confirm"))) return;
        try {
            await api.delete(`/voucher-audit/reports/${rpt.id}`);
            toast.success(t("voucher_audit.reports.deleted"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("voucher_audit.reports.delete_failed"));
        }
    };

    const duplicate = (rpt) => {
        setEditing(null);
        setForm({ ...emptySchedule, ...rpt, name: `${rpt.name} (copy)` });
        setRecipientsRaw((rpt.recipients || []).join(", "));
        setOpen(true);
    };

    const togglePause = async (rpt) => {
        try {
            await api.put(`/voucher-audit/reports/${rpt.id}`, { ...rpt, active: !rpt.active });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("voucher_audit.reports.update_failed"));
        }
    };

    const sendTest = async (rpt) => {
        try {
            const res = await api.post(`/voucher-audit/reports/${rpt.id}/send-test`, {});
            if (res.data?.status === "ok") toast.success(t("voucher_audit.reports.test_sent"));
            else toast.error(res.data?.error || t("voucher_audit.reports.test_failed"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("voucher_audit.reports.test_failed"));
        }
    };

    const downloadPreview = async (fmt) => {
        try {
            const res = await api.get(`/voucher-audit/preview?group_by=employee_code&export_format=${fmt}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `voucher-audit.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(t("voucher_audit.reports.download_failed"));
        }
    };

    return (
        <div className="space-y-6" data-testid="voucher-audit-reports-panel" data-insight-id="vouchers.audit.scheduled-reports">
            {/* v4.10.0 — Lightweight first-time guidance (dismissible, reopen via help glyph) */}
            <SetupGuidanceCard
                guideKey="voucher_audit"
                points={[
                    "setup_guide.voucher_audit.point_schedule",
                    "setup_guide.voucher_audit.point_group",
                    "setup_guide.voucher_audit.point_reconcile",
                ]}
                testId="setup-guide-voucher-audit"
            />

            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <CalendarClock size={18} strokeWidth={1.5} className="text-amber-500" />
                        {t("voucher_audit.reports.title")}
                        <HelpGlyph guideKey="voucher_audit" testId="voucher-audit-help-glyph" />
                    </h3>
                    <p className="text-sm text-muted-foreground">{t("voucher_audit.reports.subtitle")}</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadPreview("csv")} data-testid="voucher-audit-export-csv">
                        <FileText size={14} className="mr-1.5" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadPreview("xlsx")} data-testid="voucher-audit-export-xlsx">
                        <FileSpreadsheet size={14} className="mr-1.5" /> XLSX
                    </Button>
                    <Button onClick={openCreate} data-testid="voucher-audit-new-report-btn">
                        <Plus size={14} className="mr-1.5" /> {t("voucher_audit.reports.new_button")}
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                        <tr className="text-left">
                            <th className="p-3 font-medium">{t("voucher_audit.reports.col_name")}</th>
                            <th className="p-3 font-medium">{t("voucher_audit.reports.col_frequency")}</th>
                            <th className="p-3 font-medium">{t("voucher_audit.reports.col_group_by")}</th>
                            <th className="p-3 font-medium">{t("voucher_audit.reports.col_recipients")}</th>
                            <th className="p-3 font-medium">{t("voucher_audit.reports.col_last_sent")}</th>
                            <th className="p-3 font-medium">{t("voucher_audit.reports.col_status")}</th>
                            <th className="p-3 font-medium w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">{t("common.loading")}</td></tr>
                        )}
                        {!loading && reports.length === 0 && (
                            <tr><td colSpan={7} className="p-6 text-center text-muted-foreground" data-testid="voucher-audit-reports-empty">
                                {t("voucher_audit.reports.empty")}
                            </td></tr>
                        )}
                        {reports.map((rpt) => (
                            <tr key={rpt.id} className="border-t" data-testid={`voucher-audit-report-${rpt.id}`}>
                                <td className="p-3 font-medium">{rpt.name}</td>
                                <td className="p-3">{t(`voucher_audit.frequency.${rpt.frequency}`)}</td>
                                <td className="p-3 text-muted-foreground">{t(`voucher_audit.group_by.${rpt.group_by}`)}</td>
                                <td className="p-3 text-xs text-muted-foreground truncate max-w-[180px]">{(rpt.recipients || []).join(", ")}</td>
                                <td className="p-3 text-xs text-muted-foreground">{rpt.last_run_at ? parseUtc(rpt.last_run_at).toLocaleString() : "—"}</td>
                                <td className="p-3">
                                    {rpt.active ? (
                                        <Badge variant="default">{t("voucher_audit.status.active")}</Badge>
                                    ) : (
                                        <Badge variant="secondary">{t("voucher_audit.status.paused")}</Badge>
                                    )}
                                </td>
                                <td className="p-3 text-right">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`voucher-audit-report-menu-${rpt.id}`}>
                                                <MoreVertical size={14} />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => openEdit(rpt)} data-testid={`voucher-audit-edit-${rpt.id}`}><Pencil size={14} className="mr-2" /> {t("common.edit")}</DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => duplicate(rpt)}><Copy size={14} className="mr-2" /> {t("voucher_audit.reports.duplicate")}</DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => togglePause(rpt)} data-testid={`voucher-audit-toggle-${rpt.id}`}>
                                                {rpt.active ? t("voucher_audit.reports.pause") : t("voucher_audit.reports.resume")}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => sendTest(rpt)} data-testid={`voucher-audit-test-${rpt.id}`}>
                                                <Send size={14} className="mr-2" /> {t("voucher_audit.reports.send_test")}
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={() => remove(rpt)} className="text-red-500" data-testid={`voucher-audit-delete-${rpt.id}`}>
                                                <Trash2 size={14} className="mr-2" /> {t("common.delete")}
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Builder modal */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="voucher-audit-builder-modal">
                    <DialogHeader>
                        <DialogTitle>{editing ? t("voucher_audit.builder.edit_title") : t("voucher_audit.builder.create_title")}</DialogTitle>
                        <DialogDescription>{t("voucher_audit.builder.subtitle")}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-3">
                        <div>
                            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.name_label")}</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="voucher-audit-builder-name" />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.frequency_label")}</Label>
                                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                                    <SelectTrigger data-testid="voucher-audit-builder-frequency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {FREQUENCIES.map(f => <SelectItem key={f} value={f}>{t(`voucher_audit.frequency.${f}`)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.delivery_time_label")}</Label>
                                <Input type="time" value={form.delivery_time} onChange={(e) => setForm({ ...form, delivery_time: e.target.value })} data-testid="voucher-audit-builder-time" />
                            </div>
                        </div>

                        {form.frequency === "weekly" && (
                            <div>
                                <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.days_label")}</Label>
                                <div className="flex flex-wrap gap-1.5" data-testid="voucher-audit-builder-days">
                                    {WEEKDAYS.map(d => {
                                        const on = (form.days_of_week || []).includes(d);
                                        return (
                                            <button
                                                key={d}
                                                type="button"
                                                onClick={() => setForm({
                                                    ...form,
                                                    days_of_week: on
                                                        ? form.days_of_week.filter(x => x !== d)
                                                        : [...(form.days_of_week || []), d],
                                                })}
                                                className="text-xs px-2.5 py-1 rounded-full transition-colors"
                                                style={{
                                                    background: on ? "rgba(16,185,129,0.15)" : "rgba(255,255,255,0.04)",
                                                    color: on ? "#A7F3D0" : undefined,
                                                    border: `1px solid ${on ? "rgba(16,185,129,0.30)" : "rgba(255,255,255,0.08)"}`,
                                                }}
                                                data-testid={`voucher-audit-builder-day-${d}`}
                                            >{t(`voucher_audit.weekday.${d}`)}</button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {form.frequency === "monthly" && (
                            <div>
                                <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.day_of_month_label")}</Label>
                                <Input type="number" min={1} max={28} value={form.day_of_month}
                                       onChange={(e) => setForm({ ...form, day_of_month: parseInt(e.target.value || 1, 10) })} />
                            </div>
                        )}

                        <div>
                            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.timezone_label")}</Label>
                            <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="America/New_York" />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.group_by_label")}</Label>
                                <Select value={form.group_by} onValueChange={(v) => setForm({ ...form, group_by: v })}>
                                    <SelectTrigger data-testid="voucher-audit-builder-group-by"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {GROUP_BYS.map(g => <SelectItem key={g} value={g}>{t(`voucher_audit.group_by.${g}`)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.format_label")}</Label>
                                <Select value={form.export_format} onValueChange={(v) => setForm({ ...form, export_format: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {FORMATS.map(f => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">{t("voucher_audit.builder.recipients_label")}</Label>
                            <Input value={recipientsRaw} onChange={(e) => setRecipientsRaw(e.target.value)}
                                   placeholder="accounting@, payroll@, owner@example.com"
                                   data-testid="voucher-audit-builder-recipients" />
                            <p className="text-xs text-muted-foreground mt-1">{t("voucher_audit.builder.recipients_helper")}</p>
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <p className="text-sm font-medium">{t("voucher_audit.builder.active_label")}</p>
                                <p className="text-xs text-muted-foreground">{t("voucher_audit.builder.active_helper")}</p>
                            </div>
                            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="voucher-audit-builder-active" />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                        <Button onClick={save} disabled={busy} data-testid="voucher-audit-builder-save">{busy ? t("common.saving") : t("common.save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default VoucherAuditReports;
